"use client";

import React, { useState, useEffect } from 'react';
import { PostDetail } from '@/components/post/PostDetail';
import { PostType } from '@/types/post';
import { getPost, getPostDetails, incrementViewCount } from '@/actions/posts/index';
import { Spinner } from '@nextui-org/react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

interface PostDetailViewProps {
  initialPost: PostType | null;
  postId: string;
  username: string;
  currentUser: any;
  onBack: () => void;
}

export function PostDetailView({ 
  initialPost, 
  postId, 
  username, 
  currentUser, 
  onBack 
}: PostDetailViewProps) {
  const [post, setPost] = useState<PostType | null>(initialPost);
  const [isLoading, setIsLoading] = useState(!initialPost);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        if (initialPost) {
          // We have initial data, just load additional details
          setIsLoadingDetails(true);
          
          // Increment view count (non-blocking)
          incrementViewCount(postId).catch(error => {
            console.warn('Failed to increment view count:', error);
          });

          // Load additional details
          const details = await getPostDetails(postId);
          if (details) {
            setPost(prev => prev ? ({
              ...prev,
              poll: details.poll,
              Post: details.Post
            }) : null);
          }
          setIsLoadingDetails(false);
        } else {
          // No initial data, need to fetch everything
          setIsLoading(true);
          
          const fetchedPost = await getPost(postId);
          if (!fetchedPost) {
            setError('Post not found');
            setIsLoading(false);
            return;
          }

          // Verify username matches
          const actualUsername = fetchedPost.user?.username || fetchedPost.user_username || fetchedPost.username;
          if (actualUsername !== username) {
            // Update URL to correct username
            window?.history?.replaceState({}, '', `/${actualUsername}/post/${postId}`);
          }

          // Increment view count (non-blocking)
          incrementViewCount(postId).catch(error => {
            console.warn('Failed to increment view count:', error);
          });

          // Set post data
          setPost({
            ...fetchedPost,
            type: 'detail' as const,
            replies: fetchedPost.replies || [],
            likes: fetchedPost.likes || [],
            reposts: fetchedPost.reposts || [],
            files: fetchedPost.files || [],
            views: fetchedPost.views || []
          });

          setIsLoading(false);

          // Load additional details
          setIsLoadingDetails(true);
          const details = await getPostDetails(postId);
          if (details) {
            setPost(prev => prev ? ({
              ...prev,
              poll: details.poll,
              Post: details.Post
            }) : null);
          }
          setIsLoadingDetails(false);
        }
      } catch (err) {
        console.error('Error loading post:', err);
        setError('Failed to load post');
        setIsLoading(false);
        setIsLoadingDetails(false);
      }
    };

    loadPost();
  }, [postId, username, initialPost]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen space-y-4">
        <div className="text-red-500 text-center">{error}</div>
        <Button onClick={onBack} variant="outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Feed
        </Button>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="w-full h-full">
      {/* Header with back button */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-sm border-b px-4 py-3">
        <div className="flex items-center space-x-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="p-2"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl font-semibold">Post</h1>
        </div>
      </div>

      {/* Post content */}
      <div className="max-w-2xl mx-auto p-4">
        <PostDetail 
          post={post}
          currentUser={currentUser}
          isLoadingDetails={isLoadingDetails}
        />
      </div>
    </div>
  );
}